// src/oura/businesses/focus.ts
//
// What to act on first, per client (Jack, 5 Nov 2026: the Businesses page
// "doesn't help me approach and prioritise"). One rule, read top to bottom:
// the client is waiting on us, the client has no dates, our work is late, we
// have gone quiet, the score is the weakest, the renewal is close. Each client
// gets every reason that applies, worst first; the list ranks clients by
// their worst one. Nothing here is new data: it reads the row the table
// already has, plus the Tracker's card when Delivery is on.

import type { Tone } from "@oura/ui";
import type { View } from "../nav";
import type { ClientCard } from "../board/model";
import { HUB_ACT_WRITES, type HubAct } from "../overview/hubStage";

export type FocusKind = "ours" | "no_dates" | "not_told" | "late" | "slipping" | "quiet" | "low_score" | "renewal" | "delivery_unknown";

/** How urgent, 0 first. Two reasons with the same rank keep the order above. */
const RANK: Record<FocusKind, number> = {
    ours: 0,
    no_dates: 1,
    not_told: 1,
    late: 2,
    slipping: 3,
    quiet: 4,
    low_score: 5,
    renewal: 6,
    delivery_unknown: 7,
};

/** The 4 status words, as the brand book has them: a reason is ours to do, or a heads-up. */
export const FOCUS_TONE: Record<FocusKind, Tone> = {
    ours: "blue",
    no_dates: "amber",
    not_told: "amber",
    late: "red",
    slipping: "amber",
    quiet: "amber",
    low_score: "rose",
    renewal: "amber",
    delivery_unknown: "gray",
};

export interface Focus {
    kind: FocusKind;
    rank: number;
    /** The thing to do, verb first ("Publish the schedule"). */
    title: string;
    /** Why, in a few words ("Client has no dates yet"). */
    why: string;
    tone: Tone;
    /** Where on the client a press lands, when it is not the row's own action. */
    view?: View;
    /** True when the row's own action (the Next card's verb) does it. */
    rowAction?: boolean;
    /** That action starts a write, so maintenance disables it (#3561). */
    rowWrites?: boolean;
    late?: boolean;
}

/** The facts a client's reasons are read from: all on the Businesses row already. */
export interface FocusFacts {
    /** The row's one action, the Next card's verb, when it is ours to press. */
    action: { label: string; act?: HubAct } | null;
    /** Its onboarding line ("Results and plan not sent") while set-up is open. */
    onboarding: { line: string; who: string; late: boolean; cta?: string | null } | null;
    /** Still being set up, its plan not started: only its set-up step counts, the rest has not begun. */
    settingUp: boolean;
    awaitingPlan: boolean;
    changePending: boolean;
    aeo: number | null;
    /** Days since the later of the last scan and the last delivery; null = never. */
    quietDays: number | null;
    endsInDays: number | null;
    /** The Tracker's read of its Delivery, when there is one. */
    card?: ClientCard | null;
    /** Its Delivery could not be read: late work is unknown, so the client is never "on track". */
    deliveryUnknown?: boolean;
}

export const QUIET_DAYS = 14;
export const LOW_SCORE = 25;
export const RENEWAL_DAYS = 60;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function lateWhy(card: ClientCard, today: string): string {
    const oldest = card.late.map((i) => i.due as string).sort()[0];
    const days = oldest ? Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${oldest}T00:00:00Z`)) / 86_400_000) : 0;
    const onClient = card.lateOnClient > 0 ? `, ${card.lateOnClient} on the client` : "";
    return `${plural(card.late.length, "item")} late${days > 0 ? `, oldest ${plural(days, "day")}` : ""}${onClient}`;
}

/** Every reason that applies to one client, worst first. Empty = on track. */
export function focusesOf(f: FocusFacts, today: string): Focus[] {
    const out: Focus[] = [];
    const add = (kind: FocusKind, rest: Omit<Focus, "kind" | "rank" | "tone">) =>
        out.push({ kind, rank: RANK[kind], tone: FOCUS_TONE[kind], ...rest });

    const ourStep = f.onboarding?.who === "staff" ? f.onboarding : null;
    if (f.action && (!f.onboarding || ourStep)) {
        add("ours", { title: f.action.label, why: f.onboarding?.line ?? "Waiting on us", rowAction: true, rowWrites: !!f.action.act && HUB_ACT_WRITES.has(f.action.act.kind), late: f.onboarding?.late ?? false });
    } else if (ourStep) {
        add("ours", { title: ourStep.cta ?? ourStep.line, why: ourStep.line, view: "onboarding", late: ourStep.late });
    }
    // Dates, Delivery, quiet days and renewals start with the plan.
    if (f.settingUp) return out;
    // "Publish" as the row's own action already says this one.
    if (f.awaitingPlan && f.action?.label !== "Publish") {
        add("no_dates", { title: "Publish the schedule", why: "Client has no dates yet", view: "timeline" });
    }
    if (f.changePending) add("not_told", { title: "Tell the client about the plan change", why: "Plan edited, client not told", view: "timeline" });
    if (f.card?.health === "at_risk") add("late", { title: "Catch up the late work", why: lateWhy(f.card, today), view: "delivery", late: true });
    else if (f.card?.health === "slipping") add("slipping", { title: "Catch up the late work", why: lateWhy(f.card, today), view: "delivery" });
    if (f.quietDays === null || f.quietDays >= QUIET_DAYS) {
        add("quiet", {
            title: "Get work moving",
            why: f.quietDays === null ? "Nothing done yet" : `Nothing done in ${plural(f.quietDays, "day")}`,
            view: "delivery",
        });
    }
    if (f.deliveryUnknown) add("delivery_unknown", { title: "Check Delivery", why: "Couldn't read its Delivery", view: "delivery" });
    if (f.aeo !== null && f.aeo < LOW_SCORE) add("low_score", { title: "Lift the AEO score", why: `${f.aeo} of 100`, view: "questions" });
    if (f.endsInDays !== null && f.endsInDays >= 0 && f.endsInDays <= RENEWAL_DAYS) {
        add("renewal", { title: "Raise the renewal", why: `Ends in ${plural(f.endsInDays, "day")}`, view: "contract" });
    }
    return out.sort((a, b) => a.rank - b.rank);
}

/** Clients ranked by their worst reason, then by how many they have, then by name. */
export function rankClients<T extends { name: string; focuses: Focus[] }>(rows: T[]): T[] {
    const worst = (r: T) => (r.focuses.length ? r.focuses[0].rank + (r.focuses[0].late ? -0.5 : 0) : 99);
    return [...rows].sort((a, b) => worst(a) - worst(b) || b.focuses.length - a.focuses.length || a.name.localeCompare(b.name));
}

/** A score's band: rose under 50, amber from 50, green from 70 (the table's own dots). */
export function band(v: number): "low" | "mid" | "good" {
    return v >= 70 ? "good" : v >= 50 ? "mid" : "low";
}
